// BusinessInsights.js — monthly income / cost / contribution analysis per
// branch, with fleet comparison (z-scores), 12-month trend charts, outlier
// identification, and BI-series insights/recommendations. Hand-rolled SVG
// charts (no chart library). Names respect the Hide-names toggle.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useMaskNames, displayName } from "./nameMask";

const API = "https://api.pharmacegroup.co.uk";

function authHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}
const gbp = (p) => (p === null || p === undefined) ? "—" : `£${(p / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const gbpP = (p) => (p === null || p === undefined) ? "—" : `£${(p / 100).toFixed(2)}`;
const num = (n) => (n === null || n === undefined) ? "—" : n;
const zColor = (z) => z === null || z === undefined ? "#94a3b8" : Math.abs(z) >= 2 ? "#dc2626" : Math.abs(z) >= 1 ? "#d97706" : "#0f766e";
const POLARITY = {
  warning: { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b" },
  opportunity: { bg: "#e0e7ff", border: "#a5b4fc", text: "#3730a3" },
  commendation: { bg: "#dcfce7", border: "#86efac", text: "#166534" },
  note: { bg: "#f3f4f6", border: "#d1d5db", text: "#374151" }
};
const monthLabel = (y, m) => `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1]} ${String(y).slice(2)}`;
function lastMonths(n = 12) {
  const out = []; const now = new Date();
  let y = now.getUTCFullYear(), m = now.getUTCMonth();
  for (let i = 0; i < n; i++) { m--; if (m < 0) { m = 11; y--; } out.unshift({ year: y, month: m + 1 }); }
  return out;
}

/* ---------- Hand-rolled SVG line chart (multi-series, 12 months) ---------- */
function LineChart({ title, series, labels, format = gbp, height = 170 }) {
  // series: [{ name, color, values: (number|null)[] }]
  const W = 640, H = height, padL = 58, padR = 10, padT = 18, padB = 26;
  const all = series.flatMap(s => s.values).filter(v => typeof v === "number");
  if (all.length === 0) return null;
  const min = Math.min(0, ...all), max = Math.max(...all) || 1;
  const x = (i) => padL + (i / Math.max(1, labels.length - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - min) / (max - min || 1)) * (H - padT - padB);
  const ticks = [min, min + (max - min) / 2, max];
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", padding: "10px 12px" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{title}</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="#f1f5f9" />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{format(Math.round(t))}</text>
          </g>
        ))}
        {labels.map((l, i) => (i % 2 === 0 &&
          <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#94a3b8">{l}</text>
        ))}
        {series.map((s, si) => {
          const pts = s.values.map((v, i) => (typeof v === "number" ? `${x(i)},${y(v)}` : null)).filter(Boolean).join(" ");
          return (
            <g key={si}>
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2" />
              {s.values.map((v, i) => typeof v === "number" &&
                <circle key={i} cx={x(i)} cy={y(v)} r="2.5" fill={s.color}><title>{`${s.name} ${labels[i]}: ${format(v)}`}</title></circle>)}
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#374151" }}>
        {series.map((s, i) => <span key={i}><span style={{ display: "inline-block", width: 10, height: 3, background: s.color, verticalAlign: "middle", marginRight: 5 }} />{s.name}</span>)}
      </div>
    </div>
  );
}

/* ---------- Outlier bar chart: branches ranked, coloured by z ---------- */
function OutlierBars({ title, rows, format, masked }) {
  // rows: [{ name, value, z }] sorted by value
  const max = Math.max(...rows.map(r => Math.abs(r.value || 0)), 1);
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", padding: "10px 12px" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>{title}
        <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>amber |z|≥1 · red |z|≥2</span></div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <span style={{ width: 170, fontSize: 11, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayName(r.name, masked)}
          </span>
          <div style={{ flex: 1, height: 12, background: "#f8fafc", borderRadius: 3, position: "relative" }}>
            <div style={{ width: `${Math.min(100, Math.abs(r.value || 0) / max * 100)}%`, height: "100%", borderRadius: 3, background: zColor(r.z), opacity: 0.85 }} />
          </div>
          <span style={{ width: 90, fontSize: 11, color: zColor(r.z), textAlign: "right", fontWeight: Math.abs(r.z || 0) >= 1 ? 700 : 400 }}>
            {format(r.value)}{r.z !== null && r.z !== undefined ? ` (z ${r.z})` : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

const METRICS = [
  { key: "contributionPence", label: "Contribution (£)", format: gbp, fromDoc: d => d.contributionPence, z: d => d.z?.contributionPence },
  { key: "staffCostPerItemPence", label: "Staff cost per item", format: gbpP, fromDoc: d => d.ratios.staffCostPerItemPence, z: d => d.z?.staffCostPerItemPence },
  { key: "itemsPerStaffHour", label: "Items per staff-hour", format: num, fromDoc: d => d.ratios.itemsPerStaffHour, z: d => d.z?.itemsPerStaffHour },
  { key: "serviceIncomePer1kItemsPence", label: "Service income / 1k items", format: gbpP, fromDoc: d => d.ratios.serviceIncomePer1kItemsPence, z: d => d.z?.serviceIncomePer1kItemsPence }
];

export default function BusinessInsights() {
  const masked = useMaskNames();
  const months = useMemo(() => lastMonths(12), []);
  const [sel, setSel] = useState(months[months.length - 1]);
  const [data, setData] = useState(null);      // { orgDoc, branchDocs, series }
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("contributionPence");
  const [openBranch, setOpenBranch] = useState(null); // pharmacyId string
  const isAll = (localStorage.getItem("organisationName") || "") === "Pharmace all";
  const [targetOrg, setTargetOrg] = useState(localStorage.getItem("bi_targetOrg") || "");

  const needsTarget = isAll && !targetOrg.trim();

  const load = useCallback(async (refresh = false) => {
    if (isAll && !targetOrg.trim()) { setData(null); setLoading(false); return; }
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const body = { year: sel.year, month: sel.month, ...(isAll && targetOrg ? { organisationName: targetOrg } : {}) };
      const res = await fetch(`${API}/api/business/summary/${refresh ? "refresh" : "org"}`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setData(d);
      if (isAll && targetOrg) localStorage.setItem("bi_targetOrg", targetOrg);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [sel, isAll, targetOrg]);

  useEffect(() => { load(false); }, [load]);

  const branchDocs = data?.branchDocs || [];
  const series = data?.series || [];
  const labels = series.map(s => monthLabel(s.year, s.month));
  const met = METRICS.find(m => m.key === metric);
  const outlierRows = branchDocs
    .map(d => ({ name: d.pharmacyName, value: met.fromDoc(d), z: met.z(d), id: String(d.pharmacyId) }))
    .filter(r => r.value !== null && r.value !== undefined)
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  const allInsights = branchDocs.flatMap(d => d.insights.map(i => ({ ...i, pharmacyName: d.pharmacyName, pharmacyId: String(d.pharmacyId) })))
    .sort((a, b) => (b.severity || 0) - (a.severity || 0));

  const card = (label2, value, sub) => (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", padding: "10px 14px", minWidth: 150 }}>
      <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>{label2}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#6b7280" }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Business Insights</h2>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Monthly income, costs and contribution per branch — assumptions from Settings → Organisation.
            Figures carry basis tags; treat flagged branches with caution.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {isAll && (
            <input value={targetOrg} onChange={e => setTargetOrg(e.target.value)} placeholder="Organisation name…"
              style={{ fontSize: 13, padding: "6px 10px", border: "1px solid #9ca3af", borderRadius: 4, width: 180 }} />
          )}
          <select value={`${sel.year}-${sel.month}`} onChange={e => { const [y, m] = e.target.value.split("-").map(Number); setSel({ year: y, month: m }); }}
            style={{ fontSize: 13, padding: "6px 10px", border: "1px solid #9ca3af", borderRadius: 4 }}>
            {months.map(m => <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{monthLabel(m.year, m.month)}</option>)}
          </select>
          <button onClick={() => load(true)} disabled={refreshing}
            style={{ border: "1px solid #4338ca", color: "#4338ca", background: "#fff", borderRadius: 4, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: refreshing ? "wait" : "pointer" }}>
            {refreshing ? "Recomputing…" : "↻ Recompute month"}
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#dc2626", fontSize: 13, margin: "12px 0" }}>Error: {error}</div>}
      {needsTarget ? (
        <div style={{ color: "#374151", padding: 24, fontSize: 14, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, marginTop: 14 }}>
          You're signed in as <b>Pharmace all</b> — type the target organisation's name in the box above
          (exactly as it appears in the fleet, e.g. "Southtown Group") and the analysis will load for that group.
          Business Insights is per-organisation; there is no meaningful all-fleet view because each group has its own financial assumptions.
        </div>
      ) : loading ? <div style={{ color: "#6b7280", padding: 24 }}>Loading…</div> : !data?.orgDoc ? (
        <div style={{ color: "#6b7280", padding: 24 }}>
          No analysis computed for this month yet — click "Recompute month" to build it now
          (first set pay rates and item income in Settings → Organisation, or every branch will carry basis flags).
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0" }}>
            {card("Fleet contribution", gbp(data.orgDoc.totals.contributionPence), `${data.orgDoc.branchCount} branches`)}
            {card("Income", gbp(data.orgDoc.totals.incomePence))}
            {card("Costs", gbp(data.orgDoc.totals.costsPence))}
            {card("Items", (data.orgDoc.totals.items || 0).toLocaleString())}
            {card("Warnings", data.orgDoc.warningCount, `${data.orgDoc.opportunityCount} opportunities`)}
          </div>

          {/* Trend charts */}
          {series.length >= 2 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, marginBottom: 14 }}>
              <LineChart title="Fleet contribution — trailing months" labels={labels}
                series={[{ name: "Contribution", color: "#0f766e", values: series.map(s => s.totals.contributionPence) }]} />
              <LineChart title="Income vs costs — trailing months" labels={labels}
                series={[
                  { name: "Income", color: "#4338ca", values: series.map(s => s.totals.incomePence) },
                  { name: "Costs", color: "#dc2626", values: series.map(s => s.totals.costsPence) }
                ]} />
            </div>
          )}

          {/* Outliers */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "4px 0 8px" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Outlier view:</span>
            {METRICS.map(m => (
              <button key={m.key} onClick={() => setMetric(m.key)}
                style={{ border: metric === m.key ? "2px solid #0f766e" : "1px solid #cbd5e1", background: metric === m.key ? "#ccfbf1" : "#fff", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
                {m.label}
              </button>
            ))}
          </div>
          <OutlierBars title={`${met.label} by branch — ${monthLabel(sel.year, sel.month)}`} rows={outlierRows} format={met.format} masked={masked} />

          {/* Insights feed */}
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: "16px 0 6px" }}>
            Insights & recommendations ({allInsights.length})
          </div>
          {allInsights.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>No outliers or warnings this month.</div>
          ) : allInsights.map((i, ix) => {
            const st = POLARITY[i.polarity] || POLARITY.note;
            return (
              <div key={ix} onClick={() => setOpenBranch(i.pharmacyId)}
                style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6, padding: "8px 12px", marginBottom: 6, cursor: "pointer" }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: st.text }}>
                  {i.id} · {displayName(i.pharmacyName, masked)} — {i.title}
                </span>
                <div style={{ fontSize: 13, color: "#1f2937", marginTop: 2 }}>{i.detail}</div>
              </div>
            );
          })}

          {/* Branch drill-down */}
          {openBranch && (() => {
            const d = branchDocs.find(b => String(b.pharmacyId) === openBranch);
            if (!d) return null;
            const branchSeriesVals = series.map(() => null); // per-branch history could be added via extra endpoint later
            return (
              <div onClick={() => setOpenBranch(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 8, width: "min(760px, 94vw)", maxHeight: "88vh", overflowY: "auto", padding: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{displayName(d.pharmacyName, masked)} — {monthLabel(d.year, d.month)}</div>
                    <button onClick={() => setOpenBranch(null)} style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 4, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>✕ Close</button>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    {card("Contribution", gbp(d.contributionPence), d.ratios.contributionMarginPct !== null ? `${d.ratios.contributionMarginPct}% margin` : null)}
                    {card("Income", gbp(d.income.totalPence), `NHS ${gbp(d.income.nhsItemsPence)} · OTC ${gbp(d.income.otcPence)} · Svcs ${gbp(d.income.servicesPence)}`)}
                    {card("Costs", gbp(d.costs.totalPence), `Staff ${gbp(d.costs.staffPence)} · Locum ${gbp(d.costs.locumPence)} · Premises ${gbp(d.costs.premisesPence)}`)}
                    {card("Items", num(d.activity.items), `${num(d.activity.totalHours)} staff hours`)}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: "8px 0 4px" }}>Services</div>
                  <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 10 }}>
                    <thead><tr>{["Service", "Count", "Source", "Fee", "Income"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#6b7280", padding: "3px 8px", borderBottom: "1px solid #e5e7eb" }}>{h}</th>)}</tr></thead>
                    <tbody>{(d.activity.services || []).map((s, i) => (
                      <tr key={i}>
                        <td style={{ padding: "3px 8px", fontSize: 13 }}>{s.label}</td>
                        <td style={{ padding: "3px 8px", fontSize: 13 }}>{s.count}</td>
                        <td style={{ padding: "3px 8px", fontSize: 12, color: s.source === "manual" ? "#3730a3" : "#6b7280" }}>{s.source === "manual" ? "manual (periodic entry)" : s.source}</td>
                        <td style={{ padding: "3px 8px", fontSize: 13 }}>{gbpP(s.feePence)}</td>
                        <td style={{ padding: "3px 8px", fontSize: 13, fontWeight: 700 }}>{gbp(s.incomePence)}</td>
                      </tr>))}</tbody>
                  </table>
                  {(d.basisFlags || []).length > 0 && (
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Data gaps this month</div>
                      {d.basisFlags.map((f, i) => <div key={i} style={{ fontSize: 12, color: "#78350f" }}>• {f}</div>)}
                    </div>
                  )}
                  {(d.insights || []).map((i, ix) => {
                    const st = POLARITY[i.polarity] || POLARITY.note;
                    return (
                      <div key={ix} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: st.text }}>{i.id} — {i.title}</span>
                        <div style={{ fontSize: 13, color: "#1f2937", marginTop: 2 }}>{i.detail}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}